import { useEffect, useMemo, useState } from "react";

import {
  ArrowButton,
  TextField,
  Select,
  TableMetadata,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models/Export/ExportTypeEnum";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { RequestCountyFilterSelect } from "./components";
import { REQUEST_COUNTY_FILTER_ACCESS } from "./constants";
import {
  createInitialInstitutionIds,
  createInitialRequestDataSearch,
  getInitialTableState,
  sanitizeRequestSearchApiPayload
} from "./utils";
import {
  searchRequestData,
  getRequestDataExport,
} from "../../api/Request/RequestApi";
import {
  ContentArea,
  InstitutionSelect,
  ListTable,
  SearchCard,
  ToolbarTop,
} from "../../components";
import {
  useInput,
  useFilterPersistence,
  useAuth,
  useEnumCodeBooks,
  useTableStatePersistence,
} from "../../hooks";
import { Role } from "../../models";
import {
  LanguageCourseDecisionStatus,
  RequestData,
  RequestDataSearch,
  RequestSort,
} from "../../models/Request";
import { ClaimType } from "../../models/User";
import {
  useGetSchoolYearCurrentQuery,
  useGetSchoolYearListQuery,
} from "../../queries/codeBookQuery";
import { languageCourseDecisionMinistryEditUrl } from "../../routes/routes";
import { FormatUtils, TableUtils } from "../../utils";

const STORAGE_KEY = "LanguageCourseDecisionMinistrySearch";

export function LanguageCourseDecisionMinistryList() {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleChange } = useInput();
  const auth = useAuth();

  const {
    requestTypeCodeBook,
    requestStatusCodeBook,
    languageCourseDecisionStatusCodeBook,
  } = useEnumCodeBooks();

  const { data: schoolYear } = useGetSchoolYearCurrentQuery();

  const INITIAL_TABLE_STATE = useMemo(
    () => getInitialTableState(STORAGE_KEY),
    []
  );

  const INITIAL_INSTITUTION_IDS = useMemo(
    () => createInitialInstitutionIds(auth.currentRole),
    [auth.currentRole]
  );

  const INITIAL_REQUEST_DATA_SEARCH = useMemo(
    () => ({
      ...createInitialRequestDataSearch({
        schoolYearId: schoolYear?.id,
        initialTableState: INITIAL_TABLE_STATE,
        defaultInstitutionIds: INITIAL_INSTITUTION_IDS,
      }),
      onlyMinistryLanguageCourseRequired: true,
    }),
    [schoolYear?.id, INITIAL_TABLE_STATE, INITIAL_INSTITUTION_IDS]
  );

  //TABLE DATA
  const defaultTableData = useMemo(
    () => TableUtils.tableDefaultData<RequestData>(),
    []
  );
  const [tableData, setTableData] =
    useState<PagedResult<RequestData>>(defaultTableData);

  //SET SEARCH FILTERS
  const {
    filterState: requestDataSearch,
    setFilterState: setRequestDataSearch,
    saveFilters,
    clearFilters,
    filterChanged,
    setClearFilter,
  } = useFilterPersistence<RequestDataSearch>({
    storageKey: STORAGE_KEY,
    initialState: INITIAL_REQUEST_DATA_SEARCH,
  });

  //TABLE METADATA
  const tableMetadata: TableMetadata<RequestData> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "code",
        header: t("common.schoolYearModel.code"),
        accessor: (item: RequestData) => {
          return <div className={"text-nowrap text-right"}>{item.code}</div>;
        },
        sortBy: RequestSort.Code,
      },
      {
        columnKey: "firstName",
        header:
          t("common.schoolCandidateModel.lastName") +
          "/" +
          t("common.schoolCandidateModel.firstName"),
        accessor: (item: RequestData) => {
          return (
            <div>
              <div className={"text-nowrap"}>
                {item.lastName},&nbsp;{item.firstName}
              </div>
              <div className={"text-nowrap"}>
                {t("common.schoolCandidateModel.oib")}:&nbsp;{item.childOib}
              </div>
            </div>
          );
        },
        sortBy: RequestSort.LastName,
      },
      {
        columnKey: "dateOfBirth",
        header: t("common.schoolCandidateModel.dateOfBirth"),
        accessor: (item: RequestData) => {
          return (
            <div className={"text-nowrap text-right"}>
              {item.dateOfBirth ? hrDate(new Date(item.dateOfBirth)) : ""}
            </div>
          );
        },
        sortBy: RequestSort.Birthday,
      },
      {
        columnKey: "permanentAddress",
        header:
          t("common.schoolCandidateModel.permanentAddress") +
          "/" +
          t("common.schoolCandidateModel.temporaryAddress"),
        accessor: (item: RequestData) => {
          return (
            <>
              <div className={"text-nowrap"}>
                <b>
                  {item.permanentAddress?.street}&nbsp;
                  {item.permanentAddress?.houseNumber},&nbsp;
                  {item.permanentAddress?.town}{" "}
                </b>
              </div>
              {item.temporaryAddress && (
                <div className={"text-nowrap"}>
                  {item.temporaryAddress.street}&nbsp;
                  {item.temporaryAddress.houseNumber},&nbsp;
                  {item.temporaryAddress.town}{" "}
                </div>
              )}
            </>
          );
        },
        sortBy: RequestSort.Address,
      },
      {
        columnKey: "type",
        header: t("common.requestModel.requestType"),
        accessor: (item: RequestData) => {
          return (
            <Label
              color={FormatUtils.getRequestTypeColor(item.requestType)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {requestTypeCodeBook.find((v) => v.id === item.requestType)?.name}
            </Label>
          );
        },
        sortBy: RequestSort.Type,
      },
      {
        columnKey: "status",
        header: t("common.requestModel.requestStatus"),
        accessor: (item: RequestData) => {
          return (
            <Label
              color={FormatUtils.getRequestStatusColor(item.status)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {requestStatusCodeBook.find((v) => v.id === item.status)?.name}
            </Label>
          );
        },
        sortBy: RequestSort.Status,
      },
      {
        columnKey: "languageDecisionStatus",
        header: t("common.requestModel.languageDecisionStatus"),
        accessor: (item: RequestData) => {
          return (
            <>
              {
                languageCourseDecisionStatusCodeBook.find(
                  (v) => v.id === item.languageCourseDecisionStatus
                )?.name
              }
            </>
          );
        },
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: RequestData) => {
          if (
            auth.currentRole?.claimType === ClaimType.County ||
            auth.currentRole?.role === Role.CAS ||
            auth.currentRole?.role === Role.Helpdesk ||
            (item.languageCourseDecisionStatus ??
              LanguageCourseDecisionStatus.Generated) >
              LanguageCourseDecisionStatus.Generated
          )
            return (
              <div className={"text-right"}>
                <ArrowButton
                  ariaLabel={t("common.details")}
                  onClick={() => handleDetailsClicked(item.id, languageCourseDecisionMinistryEditUrl)}
                />
              </div>
            );
          else return <></>;
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  const [tableProps, tableState, setFilters] = useTable<RequestDataSearch>({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: INITIAL_TABLE_STATE.page,
    paginationPageSize: INITIAL_TABLE_STATE.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: INITIAL_TABLE_STATE.order,
      sortBy: INITIAL_TABLE_STATE.sortBy,
    },
  });
  //FILTER DATA
  const { data: schoolYears } = useGetSchoolYearListQuery();

  const { handleDetailsClicked } = useTableStatePersistence({
    storageKey: STORAGE_KEY,
    requestDataSearch,
    tableState,
  });

  const canApplyCountyFilter = useMemo(
    () => auth.hasRoleAccess(REQUEST_COUNTY_FILTER_ACCESS),
    [auth]
  );

  useEffect(() => {
    if (!requestDataSearch.schoolYearId && schoolYear && schoolYear.id) {
      setRequestDataSearch({
        ...requestDataSearch,
        schoolYearId: schoolYear.id,
      });
    }
  }, [schoolYear, requestDataSearch.schoolYearId]);

  useEffect(() => {
    (async () => {
      const request = TableUtils.GetSearchRequest<RequestDataSearch>(
        requestDataSearch,
        tableState
      );
      const itemList = await searchRequestData(
        sanitizeRequestSearchApiPayload(request, canApplyCountyFilter)
      );
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState, canApplyCountyFilter]);

  const handleInstitutionChange = (id: number) => {
    const newSearch: RequestDataSearch = {
      ...requestDataSearch,
      institutionIds: !isNaN(id) ? [id] : [],
    };
    setRequestDataSearch(newSearch);
  };

  const handleSearchClicked = () => {
    doSearch();
  };
  const handleClearSearchClicked = () => {
    const newRequestDataSearch: RequestDataSearch = {
      schoolYearId: schoolYear?.id,
      institutionIds: INITIAL_INSTITUTION_IDS,
      countyId: undefined,
      onlyRequests: false,
      onlyWithAddReasonDelayPreviousYear: false,
      onlyNotSchoolCandidate: false,
      onlyOutsideEnrollmentArea: false,
      onlyLanguageCourseRequired: false,
      onlyMinistryLanguageCourseRequired: true,
      onlyEnrollmentApproved: false,
      onlyEnrolled: false,
      onlyOutsideEnrollmentAreaOnlyEditable: false,
      outsideEnrollmentAreaStatus:
        INITIAL_TABLE_STATE.outsideEnrollmentAreaStatus,
    };

    setClearFilter(true);
    setRequestDataSearch(newRequestDataSearch);
    clearFilters();
    doSearch();
  };
  const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
    await getRequestDataExport(
      sanitizeRequestSearchApiPayload(
        requestDataSearch,
        canApplyCountyFilter
      ),
      exportType
    );
  };
  const doSearch = () => {
    setFilters({ ...requestDataSearch });
  };

  return (
    <ContentArea>
      <ToolbarTop
        pageTitle={t("common.requestModel.pageName")}
        showAdd={"hide"}
      />
      <SearchCard
        onClickSearch={handleSearchClicked}
        onClickClearSearch={handleClearSearchClicked}
        isFilterChanged={filterChanged}
        cardTitle={t("common.requestModel.searchCardName")}
        hideDivider={true}
      >
        <Row>
          <Col md={12}>
            <Select
              label={t("common.schoolCandidateModel.schoolYearId")}
              name={nameof<typeof requestDataSearch>("schoolYearId")}
              value={requestDataSearch.schoolYearId}
              required={true}
              onChange={handleChange(setRequestDataSearch)}
              selectData={codeBookToSelectData(schoolYears)}
            />
          </Col>
        </Row>
        {canApplyCountyFilter && (
          <Row>
            <Col md={6}>
              <RequestCountyFilterSelect
                value={requestDataSearch.countyId}
                setFilterState={setRequestDataSearch}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={8}>
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={nameof<typeof requestDataSearch>("institutionIds")}
              value={
                requestDataSearch.institutionIds &&
                requestDataSearch.institutionIds.length > 0
                  ? requestDataSearch.institutionIds[0]
                  : undefined
              }
              countyIds={
                auth.currentRole?.claimType === ClaimType.County
                  ? auth.currentRole.locations
                  : undefined
              }
              ids={
                auth.currentRole?.claimType === ClaimType.Institution
                  ? auth.currentRole.locations
                  : undefined
              }
              onChange={(e) =>
                handleInstitutionChange(Number.parseInt(e.target.value))
              }
              showUser={true}
              shouldCheckParentCounty
            />
          </Col>
          <Col md={3}>
            <TextField
              label={t("common.schoolYearModel.code")}
              name={nameof<typeof requestDataSearch>("code")}
              value={requestDataSearch.code}
              onChange={handleChange(setRequestDataSearch)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.lastName")}
              name={nameof<typeof requestDataSearch>("lastName")}
              value={requestDataSearch.lastName}
              onChange={handleChange(setRequestDataSearch)}
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.firstName")}
              name={nameof<typeof requestDataSearch>("firstName")}
              value={requestDataSearch.firstName}
              onChange={handleChange(setRequestDataSearch)}
            />
          </Col>
          <Col md={3}>
            <TextField
              label={t("common.schoolCandidateModel.oib")}
              name={nameof<typeof requestDataSearch>("oib")}
              value={requestDataSearch.oib}
              onChange={handleChange(setRequestDataSearch)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <Select
              label={t("common.requestModel.requestType")}
              name={nameof<typeof requestDataSearch>("requestType")}
              value={requestDataSearch.requestType}
              onChange={handleChange(setRequestDataSearch)}
              selectData={codeBookToSelectData(requestTypeCodeBook)}
              emptyOption={{ text: t("common.all"), value: undefined }}
            />
          </Col>
          <Col md={3}>
            <Select
              label={t("common.requestModel.requestStatus")}
              name={nameof<typeof requestDataSearch>("requestStatus")}
              value={requestDataSearch.requestStatus}
              onChange={handleChange(setRequestDataSearch)}
              selectData={codeBookToSelectData(requestStatusCodeBook)}
              emptyOption={{ text: t("common.all"), value: undefined }}
            />
          </Col>
        </Row>
      </SearchCard>
      <ListTable
        tableProps={tableProps}
        onClickDownload={(exportType: ExportTypeEnum) =>
          handleDownloadClicked(exportType)
        }
        showDownload
      />
    </ContentArea>
  );
}
